// src/pages/message-center/ShiftTypesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Plus, Edit, Percent } from 'lucide-react';
import { PageHeader, DataTable } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getShiftTypes } from '../../services/api/messageCenterShiftTypes';
import { formatCurrency } from '../../utils/currencyUtil';
import { getShiftTypeIcon } from './utils/shiftTypeIcon';
import ShiftTypeModal from './components/ShiftTypeModal';
import ShiftLeaderCommissionModal from './components/ShiftLeaderCommissionModal';
import styles from './ShiftTypesPage.module.css';

const ShiftTypesPage = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_message_center_shift_types');
  const canManageLeaderCommission = hasPermission('manage_message_center_shift_leader_commission');

  const [shiftTypes, setShiftTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    total: 0,
    pageSizeOptions: [10, 15, 25, 50],
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedShiftType, setSelectedShiftType] = useState(null);
  const [showLeaderCommissionModal, setShowLeaderCommissionModal] = useState(false);

  const loadShiftTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getShiftTypes({ page: pagination.currentPage, pageSize: pagination.pageSize });
      setShiftTypes(data.shiftTypes || []);
      setPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading shift types:', err);
      showError(err.response?.data?.message || t('messageCenterShiftTypes.failedToLoadShiftTypes'));
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadShiftTypes();
  }, [loadShiftTypes]);

  const handleCreate = () => {
    setModalMode('create');
    setSelectedShiftType(null);
    setShowModal(true);
  };

  const handleEdit = (shiftType) => {
    setModalMode('edit');
    setSelectedShiftType(shiftType);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedShiftType(null);
    loadShiftTypes();
  };

  const columns = [
    {
      key: 'name',
      header: t('messageCenterShiftTypes.name'),
      render: (row) => {
        const ShiftTypeIcon = getShiftTypeIcon(row.name);
        return (
          <div className={styles.shiftTypeCell}>
            <ShiftTypeIcon size={14} />
            <span>{row.name}</span>
          </div>
        );
      },
    },
    {
      key: 'defaultTimes',
      header: t('messageCenterShiftTypes.defaultTimes'),
      width: '160px',
      render: (row) => `${row.defaultStartTime} – ${row.defaultEndTime}`,
    },
    {
      key: 'commissionMethod',
      header: t('messageCenterShiftTypes.commissionMethod'),
      width: '160px',
      render: () => t('messageCenterShiftTypes.perSalesValue'),
    },
    {
      key: 'defaultTarget',
      header: t('messageCenterShiftTypes.defaultTarget'),
      width: '140px',
      render: (row) => row.defaultTarget != null
        ? formatCurrency(row.defaultTarget) 
        : <span className={styles.na}>{t('common.na')}</span>,
    },
    {
      key: 'commissionSlabs',
      header: t('messageCenterShiftTypes.commissionSlabs'),
      render: (row) => {
        const slabs = row.commissionSlabs;
        if (!Array.isArray(slabs) || slabs.length === 0) {
          return <span className={styles.na}>{t('messageCenterShiftTypes.noSlabsConfigured')}</span>;
        }
        return (
          <div className={styles.slabsCell}>
            {slabs.map((slab, i) => (
              <span key={i} className={styles.slabBadge}>
                <Percent size={11} />
                {slab.maxValue != null
                  ? `< ${slab.maxValue}: ${slab.rate}%`
                  : `≥ ${slab.minValue}: ${slab.rate}%`}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'isActive',
      header: t('common.status'),
      width: '110px',
      render: (row) => (
        <span className={row.isActive ? styles.activeBadge : styles.inactiveBadge}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ];

  const tableActions = canManage
    ? {
        render: (row) => (
          <div className={styles.actionButtons}>
            <button
              onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
              className={styles.actionButton}
              title={t('messageCenterShiftTypes.edit')}
            >
              <Edit size={16} />
            </button>
          </div>
        ),
        width: '70px',
        frozen: true,
      }
    : undefined;

  return (
    <div className={styles.shiftTypesPage}>
      <PageHeader
        title={t('messageCenterShiftTypes.pageTitle')}
        description={t('messageCenterShiftTypes.pageDescription')}
        icon={Settings}
        actions={
          <div className={styles.headerActions}>
            {canManageLeaderCommission && (
              <button onClick={() => setShowLeaderCommissionModal(true)} className={styles.primaryButton}>
                <Percent size={20} />
                {t('messageCenterShiftLeaderCommission.configureButton')}
              </button>
            )}
            {canManage && (
              <button onClick={handleCreate} className={styles.primaryButton}>
                <Plus size={20} />
                {t('messageCenterShiftTypes.newShiftType')}
              </button>
            )}
          </div>
        }
      />

      <DataTable
        data={shiftTypes}
        columns={columns}
        actions={tableActions}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={t('messageCenterShiftTypes.noShiftTypesYet')}
        emptyDescription={t('messageCenterShiftTypes.noShiftTypesYetDescription')}
        emptyIcon={<Settings size={64} />}
        emptyAction={
          canManage ? (
            <button onClick={handleCreate} className={styles.primaryButton}>
              <Plus size={18} />
              {t('messageCenterShiftTypes.newShiftType')}
            </button>
          ) : null
        }
      />

      {showModal && (
        <ShiftTypeModal
          mode={modalMode}
          shiftType={selectedShiftType}
          onClose={() => { setShowModal(false); setSelectedShiftType(null); }}
          onSuccess={handleModalSuccess}
        />
      )}

      {showLeaderCommissionModal && (
        <ShiftLeaderCommissionModal
          onClose={() => setShowLeaderCommissionModal(false)}
          onSuccess={() => setShowLeaderCommissionModal(false)}
        />
      )}
    </div>
  );
};

export default ShiftTypesPage;
